import type { Id } from '@e-socna/data-layer';

import type { MappedReferencesContext } from '../../../../contexts/ReferencesContext';
import { HtmlViewer } from '../../../HtmlViewer';
import { formatAmount } from '../../../../utils/format-amount.util';
import type {
  DevisPrestationsCostAmountRenderModel,
  DevisPrestationsUsageRenderModel,
} from '../utils/build-devis-prestations-render-model.util';

import { DevisPrestationsCostDetails } from './DevisPrestationsCostDetails';
import { DevisPrestationsMaterialDetails } from './DevisPrestationsMaterialDetails';
import { DevisPrestationsUsageAmounts } from './DevisPrestationsUsageAmounts';

const formatCostTotal = (
  amount: DevisPrestationsCostAmountRenderModel,
  currenciesIdMap: MappedReferencesContext['currenciesIdMap'],
): string =>
  formatAmount(
    { value: amount.value, currencyId: amount.currencyId ?? ('' as Id) },
    currenciesIdMap,
  );

interface Props {
  readonly currenciesIdMap: MappedReferencesContext['currenciesIdMap'];
  readonly unitsIdMap: MappedReferencesContext['unitsIdMap'];
  readonly usage: DevisPrestationsUsageRenderModel;
  readonly vatsIdMap: MappedReferencesContext['vatsIdMap'];
}

export const DevisPrestationsUsageDetails = ({
  currenciesIdMap,
  unitsIdMap,
  usage,
  vatsIdMap,
}: Readonly<Props>) => (
  <>
    {usage.title}
    {usage.content ? <HtmlViewer html={usage.content} /> : null}
    {usage.quantity ? <div>Quantité : {usage.quantity.value}</div> : null}
    {usage.amounts ? (
      <DevisPrestationsUsageAmounts
        amounts={usage.amounts}
        currenciesIdMap={currenciesIdMap}
        vatsIdMap={vatsIdMap}
      />
    ) : null}
    {usage.materials === null ? null : (
      <div>
        <strong>Matériels liés :</strong>
        {usage.materials.map((material) => (
          <DevisPrestationsMaterialDetails
            key={material.id}
            material={material}
            unitsIdMap={unitsIdMap}
          />
        ))}
      </div>
    )}
    {usage.costs === null ? null : (
      <div>
        <strong>Coûts liés :</strong>
        {usage.costs.map((cost) => (
          <DevisPrestationsCostDetails
            cost={cost}
            currenciesIdMap={currenciesIdMap}
            key={cost.id}
            unitsIdMap={unitsIdMap}
          />
        ))}
        {usage.costsSubtotalForOneUnit ? (
          <div>
            {'Sous-total des coûts pour une unité de service : '}
            {formatCostTotal(usage.costsSubtotalForOneUnit, currenciesIdMap)}
          </div>
        ) : null}
        {usage.costsTotal ? (
          <div>
            {'Total des coûts pour la prestation : '}
            {formatCostTotal(usage.costsTotal, currenciesIdMap)}
          </div>
        ) : null}
      </div>
    )}
  </>
);
